import { easeQuadInOut } from 'd3-ease';
import React, { useRef, useState, useLayoutEffect, ElementRef } from 'react';
import { createPortal } from 'react-dom';
import { animated, useSpring } from 'react-spring/web.cjs';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { NAVBAR_BASE_HEIGHT } from '../../../../constants/ui/navbar';
import { getPortalRootDOMElement } from '../../../../DOMElements';
import { ZIndices } from '../../../../styled-components/dark/common';
import { type SportId } from '../../../../types/sports';
import { LINE_UP_MODAL_APPEARANCE_TRANSITION_DURATION } from '../../constants/ui';
import LineUpMainViewContainer from '../LineUpMainView/LIneUpMainViewContainer';

type AnimatedWrapperType = {
  $show: boolean;
};

const AnimatedWrapper = styled(animated.div)<AnimatedWrapperType>`
  position: absolute;
  padding: 0;
  margin: 0;
  width: 100%;
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em);
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em - constant(safe-area-inset-top));
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em - env(safe-area-inset-top));
  bottom: 0px;

  left: 0;
  z-index: ${ZIndices.lineUpModal};
  transition: opacity 300ms ease-in-out;

  ${({ $show }) =>
    $show
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
`;

type MobileMatchLineUpModalProps = {
  isOpen: boolean;
  matchId: number;
  sportId: SportId;
};

const LineUpMobileModal = ({ isOpen = false, matchId, sportId }: MobileMatchLineUpModalProps) => {
  const wrapperRef = useRef<ElementRef<'div'>>(null);
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const [wrapperBottomMargin, setWrapperBottomMargin] = useState(0);
  const [delayedMatchId, setDelayedMatchId] = useState(matchId);

  useLayoutEffect(() => {
    const wrapperNode = wrapperRef.current;

    if (wrapperNode) {
      setWrapperHeight(parseFloat(window.getComputedStyle(wrapperRef.current)?.height?.slice(0, -2) ?? 0));
      setWrapperBottomMargin(parseFloat(window.getComputedStyle(wrapperRef.current)?.bottom?.slice(0, -2) ?? 0));
    }
  }, []);

  // @ts-ignore
  const [{ y }, set] = useSpring(
    () => ({
      config: {
        duration: LINE_UP_MODAL_APPEARANCE_TRANSITION_DURATION,
        easing: easeQuadInOut,
      },
    }),
    [],
  );

  useLayoutEffect(() => {
    if (typeof wrapperHeight === 'number' && typeof wrapperBottomMargin === 'number') {
      // @ts-ignore
      set(() => ({ y: isOpen ? 0 : wrapperHeight + wrapperBottomMargin + 100 }));
    }
  }, [isOpen, set, wrapperBottomMargin, wrapperHeight, matchId]);

  const matchIdIdRefTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    matchIdIdRefTimeoutRef.current = setTimeout(() => {
      setDelayedMatchId(matchId);
      matchIdIdRefTimeoutRef.current = null;
    }, LINE_UP_MODAL_APPEARANCE_TRANSITION_DURATION);
  }, [matchId]);

  useLayoutEffect(() => {
    return () => {
      if (matchIdIdRefTimeoutRef.current) {
        clearTimeout(matchIdIdRefTimeoutRef.current);
      }
    };
  }, []);

  return createPortal(
    <CSSTransition in={isOpen} unmountOnExit timeout={LINE_UP_MODAL_APPEARANCE_TRANSITION_DURATION}>
      <AnimatedWrapper style={{ y }} ref={wrapperRef} $show={isOpen}>
        <LineUpMainViewContainer matchId={delayedMatchId} sportId={sportId} />
      </AnimatedWrapper>
    </CSSTransition>,
    getPortalRootDOMElement(),
  );
};

export default LineUpMobileModal;
